import AncillaryRow from '@/components/purchase/checkout/ancillaries/AncillaryRow';
import LegOriginDestination from '@/components/purchase/checkout/LegOriginDestination';
import { useAvailableAncillaries } from '@/hooks/useAvailableAncillaries';
import { useCheckoutBooking } from '@/hooks/useCheckoutBooking';
import { TransText } from '@/i18n/trans/text';
import type { BookedAncillaryDTO, LegDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import { Accordion, Button, Icons, Typography } from '@fleet/webshop-ui';
import { type FC, useMemo } from 'react';

interface LegSectionProps {
  leg: LegDTO;
  classNames?: { accordionItem?: string };
  handleRemoveAncillary: (ancillary: BookedAncillaryDTO) => Promise<void>;
  changeSelectedLeg: () => void;
}

const LegSection: FC<LegSectionProps> = ({
  leg,
  classNames,
  handleRemoveAncillary,
  changeSelectedLeg,
}) => {
  const booking = useCheckoutBooking();

  const ancillariesByLegId = useMemo(() => {
    const ancillariesByLegId: Record<
      NonNullable<LegDTO['id']>,
      Array<BookedAncillaryDTO>
    > = {};

    booking?.bookedTrips?.forEach(({ bookedOffers }) =>
      bookedOffers?.forEach(({ admissions }) =>
        admissions?.forEach(({ coveredLegIds, ancillaries }) =>
          coveredLegIds?.forEach((id) => {
            ancillariesByLegId[id] = [
              ...(ancillariesByLegId[id] || []),
              ...(ancillaries || []),
            ];
          })
        )
      )
    );

    return ancillariesByLegId;
  }, [booking?.bookedTrips]);
  const availableAncillaries = useAvailableAncillaries(leg.id);
  const selectedAncillaries = leg.id ? ancillariesByLegId[leg.id] : [];

  return (
    <div className="flex flex-col gap-2">
      <LegOriginDestination
        origin={leg.originStop?.name ?? ''}
        destination={leg.destinationStop?.name ?? ''}
        ptMode={leg.ptMode}
      />
      {!!selectedAncillaries?.length && (
        <Accordion
          defaultExpanded
          className={cn('border-neutral-pale pb-4', classNames?.accordionItem)}
          classNames={{
            trigger:
              'my-4 justify-start gap-2 py-0 text-primary hover:no-underline',
            content: 'flex flex-col gap-3 pl-7',
          }}
          trigger={
            <>
              <Icons.addon className="h-5 w-5" />
              <Typography variant="body1-bold">
                <TransText i18nKey="addedExtras" />
              </Typography>
            </>
          }
        >
          {selectedAncillaries.map((ancillary, key) => (
            <AncillaryRow
              ancillary={ancillary}
              icon={
                <Icons.addon height={16} width={16} className="text-primary" />
              }
              handleRemoveAncillary={handleRemoveAncillary}
              key={key}
            />
          ))}
        </Accordion>
      )}

      <Button
        variant="secondary"
        data-testid="add-ancillary-button"
        className="mt-1 w-fit"
        onClick={changeSelectedLeg}
        isDisabled={!availableAncillaries.length}
        startAdornment={<Icons.addon className="h-5 w-5" />}
      >
        <TransText
          i18nKey={
            availableAncillaries.length ? 'addExtras' : 'noExtrasAvailable'
          }
        />
      </Button>
    </div>
  );
};

export default LegSection;
